import { useCallback, useMemo, useState } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { useFormattedCart } from "../useSubmitCheckout/useFormattedCart";
import { ISendToKitchenResponse } from "./types";

const useSendToKitchen = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);
	const formattedCart = useFormattedCart();
	const { state: location } = useConfigContext((state) => state.location);
	const { state: user } = useConfigContext((state) => state.user);
	const { state: qrSession } = useConfigContext((state) => state.qrSession);
	const { state: is_takeaway } = useCartContext((state) => state.is_takeaway);

	const payload = useMemo(() => {
		const host = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
		const endpoint = process.env.API_KITCHEN_ORDER ?? "";

		return {
			url: `${host}${endpoint}`,
			triggerOnMount: false,
			axiosConfig: addJWTInConfig(undefined, user?.session_id?.toString() ?? ""),
			body: {
				qr_sid: qrSession?.qrs_id ?? "",
				cart: {
					bbid: location?.id ?? "",
					is_takeaway,
					extra_info_from_order: {},
					items: formattedCart,
				},
			},
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateIfStale: false,
				shouldRetryOnError: false,
				errorRetryCount: 0,
			},
		};
	}, [location?.id, user?.session_id, qrSession?.qrs_id, is_takeaway, formattedCart]);

	const { postData } = useApiRequestPost<ISendToKitchenResponse>(payload);

	const sendToKitchen = useCallback(async (): Promise<Error | null> => {
		setIsLoading(true);
		setError(null);

		try {
			await postData();
			setIsLoading(false);

			return null;
		} catch (err) {
			const e = err instanceof Error ? err : new Error("Send to kitchen failed");

			setIsLoading(false);
			setError(e);

			return e;
		}
	}, [postData]);

	return { sendToKitchen, isLoading, error };
};

export default useSendToKitchen;
